<template>
    <DropMenu
        ref="dropmenuRef"
        :menu-state.sync="isMenuOpen"
        :value="Array.isArray(selected) ? selected : []"
        :items="items"
        :menu-props="bindProps"
        :multi="multi"
        :deselect="deselect"
        :name-key="nameKey"
        :value-key="valueKey"
        :lazy="lazy"
        :auto-focus="readonly"
        :max-height="maxHeight"
        :avatars="avatars"
        :icon-key="iconKey"
        :icon-color="iconColor"
        :searchable="searchable"
        :open-on-right="openOnRight"
        :search-value="searchValue"
        :show-selected-first="showSelectedFirst"
        :sort="false"
        @input="onInput"
        @confirm="$emit('confirm', $event)"
    >
        <template #tile-content="{ item }">
            <slot
                name="item.name"
                :item="item"
            />
        </template>
        <template v-slot:actions>
            <slot name="actions"/>
        </template>
        <template v-slot="{ on }">
            <slot :on="on">
                <div
                    v-if="multi"
                    @click="openMenu(on, $event)"
                >
                    <v-combobox
                        :value="comboboxSelected"
                        chips
                        :items="items"
                        :item-text="itemText"
                        multiple
                        :outlined="outlined"
                        deletable-chips
                        :readonly="readonly"
                        :label="label"
                        :placeholder="placeholder"
                        :disabled="disabled"
                        :append-icon="icon"
                        :hide-details="(!errorMessages || (errorMessages && !errorMessages.length)) && !forceShowDetails"
                        item-value=""
                        :menu-props="{
                            maxHeight: 0
                        }"
                        :error-messages="errorMessages"
                        dense
                        :tabindex="tabindex"
                        class="mb-0 pt-0 mt-0"
                        :class="[
                            isMenuOpen ? 'menu-open' : '',
                            inputClass
                        ]"
                        v-on="on"
                        @input="onInput"
                    >
                        <template v-slot:selection="{ attrs, item, select, selected, index }">
                            <v-chip
                                :input-value="selected"
                                class="caption"
                                color="g2"
                                :disabled="disabled"
                                small
                                style="margin-top: 2px; margin-bottom: 2px"
                            >
                                {{ item }}
                            </v-chip>
                        </template>
                        <template v-if="tooltip" v-slot:append>
                            <v-tooltip bottom>
                                <template v-slot:activator="{ on, attrs }">
                                    <v-icon
                                        size="21"
                                        class="mr-2"
                                        v-on="on"
                                    >
                                        mdi-help-circle-outline
                                    </v-icon>
                                </template>
                                {{ tooltip }}
                            </v-tooltip>
                        </template>
                    </v-combobox>
                </div>
                <v-text-field
                    v-else
                    :value="comboboxSelected"
                    :outlined="outlined"
                    :error-messages="errorMessages"
                    :hide-details="(!errorMessages || (errorMessages && !errorMessages.length)) && !forceShowDetails"
                    :label="label"
                    :placeholder="placeholder"
                    :disabled="disabled"
                    :readonly="readonly"
                    :class="[
                        isMenuOpen ? 'menu-open' : '',
                        inputClass
                    ]"
                    :tabindex="tabindex"
                    :append-icon="icon"
                    v-on="on"
                    @input="$emit('textInput', $event);"
                    @keypress="$emit('keypress', $event)"
                    @click:append="openMenu(on, $event)"
                >
                    <template v-if="tooltip" v-slot:label>
                        {{ label }}

                        <v-tooltip bottom>
                            <template v-slot:activator="{ on: tooltipOn }">
                                <div
                                    v-on="tooltipOn"
                                    class="d-inline-flex ml-1"
                                >
                                    <icon
                                        size="16"
                                        icon="HelpCircle"
                                    />
                                </div>
                            </template>
                            {{ tooltip }}
                        </v-tooltip>
                    </template>
                </v-text-field>
            </slot>
        </template>
    </DropMenu>
</template>

<script>
import {ref, computed, watch} from "@vue/composition-api";
import DropMenu from "../../NewFilterBar/components/DropMenu";

export default {
    name: "Dropdown",
    components: {
        DropMenu
    },
    props: {
        lazy: {
            type: Boolean,
            default: false
        },
        items: {
            type: Array,
            default: () => []
        },
        selected: {
            type: [Array, String, Number],
            default: () => []
        },
        inputClass: {
            type: String,
            default: null
        },
        deselect: {
            type: Boolean,
            default: false
        },
        nameKey: {
            type: String,
            default: ""
        },
        valueKey: {
            type: String,
            default: ""
        },
        label: {
            type: String,
            default: ""
        },
        errorMessages: {
            type: [Array, String],
            default: () => null
        },
        multi: {
            type: Boolean,
            default: false
        },
        readonly: {
            type: Boolean,
            default: true
        },
        placeholder: {
            type: String,
            default: ""
        },
        searchable: {
            type: Boolean,
            default: true
        },
        searchValue: {
            type: String,
            default: ''
        },
        itemText: {
            type: Function,
            default: null
        },
        disabled: {
            type: Boolean,
            default: false
        },
        inputText: {
            type: [String, Number],
            default: null
        },
        sort: {
            type: Boolean,
            default: true
        },
        outlined: {
            type: Boolean,
            default: true
        },
        menuOpenDefault: {
            type: Boolean,
            default: false
        },
        bindProps: {
            type: Object,
            default: () => ({
                'open-on-hover': false,
                'allow-overflow': false,
                'offset-y': true,
                'nudge-bottom': -4
            })
        },
        tabindex: {
            type: [String, Number],
            default: null
        },
        appendIcon: {
            type: String,
            default: null
        },
        avatars: {
            type: Boolean,
            default: true
        },
        iconKey: {
            type: String,
            default: ""
        },
        iconColor: {
            type: String,
            default: "grey"
        },
        maxHeight: {
            type: Number,
            default: null
        },
        showSelectedFirst: {
            type: Boolean,
            default: false
        },
        openOnRight: {
            type: Boolean,
            default: false
        },
        tooltip: {
            type: String,
            default: null
        },
        forceShowDetails: {
            type: Boolean,
            default: false
        }
    },
    setup(props, ctx) {
        const isMenuOpen = ref(false);
        const firstLoadMenuOpenSet = ref(false);
        const dropmenuRef = ref(null);

        const onInput = (e) => {
            if (props.multi) {
                ctx.emit("input", e);
            } else if (Array.isArray(e) && e.length) {
                ctx.emit("input", e[0]);
            } else {
                ctx.emit("input", e);
            }
        }

        const comboboxSelected = computed(() => {
            if (props.inputText) {
                return [props.inputText];
            }

            let res = []
            if (Array.isArray(props.selected)) {
                for (let selectedItem of props.selected) {
                    let item = null
                    if (typeof selectedItem === "string") {
                        let foundItem = props.items.find(x => selectedItem === x[props.valueKey])
                        if (foundItem) {
                            item = foundItem[props.nameKey]
                        } else if (!props.readonly) {
                            item = selectedItem
                        }

                    } else {
                        item = props.items.find(x => selectedItem[props.valueKey] === x[props.valueKey])[props.nameKey]
                    }

                    if (item) {
                        res.push(item)
                    }
                }
            } else {
                res = [props.selected];
            }

            return res;
        })

        const openMenu = (on, e) => {
            if (props.disabled) {
                return
            }

            if (props.appendIcon) {
                ctx.emit('click:append', e)
            } else {
                on.click(e)
                isMenuOpen.value = true
            }
        }

        watch(() => props.items, async () => {
            if (props.menuOpenDefault && !firstLoadMenuOpenSet.value && dropmenuRef.value) {
                dropmenuRef.value.$el.click()
                isMenuOpen.value = true
                firstLoadMenuOpenSet.value = true
            }
        })

        const icon = computed(() => {
            if (props.disabled) {
                return ''
            }

            if (props.appendIcon) {
                return props.appendIcon
            }

            return '$ChevronDown'
        })

        return {
            isMenuOpen,
            onInput,
            comboboxSelected,
            openMenu,
            dropmenuRef,
            icon
        }
    }
}
</script>

<style scoped lang="scss">
.v-text-field ::v-deep input {
    cursor: pointer;
}

.v-text-field ::v-deep .v-label {
    pointer-events: unset;
    display: flex;
    align-items: center;
}

.v-text-field.menu-open ::v-deep .v-input__icon--append .v-icon {
    transform: rotate(180deg);
}

</style>